import { useState } from 'react';
import { Button, OverlayTrigger, Popover, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import type { ProjectJob, ProjectResult } from '../api/types';
import { useLazyGetProjectJobQuery } from '../api/indexerApi';
import { inspectUrlsByIndexStatus } from '../lib/inspect-detail';
import { navigateToIndexWithPrefill, navigateToInspectWithPrefill } from '../lib/url-list-prefill';
import { copyText, decodeUrlForDisplay, formatFaNumber } from '../lib/utils';
import { guideTooltipHtml } from './CoverageHelpTip';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import { IndexerIcon } from './IndexerIcon';

const INDEX_REQUEST_PREFILL_LIMIT = 200;
import { INSPECT_MAX_URLS_PER_JOB } from '../lib/inspect-limits';

export type BulkNotify = (message: { variant: string; text: string }) => void;

type InspectBulkAction =
  | 'copyIndexed'
  | 'copyNotIndexed'
  | 'requestNotIndexed'
  | 'reinspectAll';

type BulkActionGuide = {
  title: string;
  paragraphs: string[];
};

const INSPECT_BULK_ACTION_GUIDES = {
  copyIndexed: {
    title: 'کپی ایندکس‌شده‌ها',
    paragraphs: [
      'تمام آدرس‌های این درخواست که از نظر گوگل ایندکس‌شده هستند را در کلیپ‌بورد کپی می‌کند.',
      'هر URL در یک خط قرار می‌گیرد؛ مناسب برای چسباندن در فایل یا ابزار دیگر.',
      'همان منطق ستون «وضعیت ایندکس» و فیلتر «ایندکس‌شده» در جزئیات است.',
    ],
  },
  copyNotIndexed: {
    title: 'کپی ایندکس‌نشده‌ها',
    paragraphs: [
      'تمام آدرسی که در این درخواست ایندکس‌شده نیست را کپی می‌کند.',
      'شامل صفحاتی که گوگل «ایندکس‌نیست» گفته (مثلاً Excluded یا not indexed) و هم آدرس‌هایی که به‌خاطر خطای API وضعیت ایندکس نگرفته‌اند.',
      'با عدد «ایندکس‌نیست» در ستون وضعیت ایندکس هم‌خوان است.',
    ],
  },
  requestNotIndexed: {
    title: 'ارسال ایندکس‌نشده‌ها برای ایندکس',
    paragraphs: [
      'همان آدرس‌های ایندکس‌نشده را به فرم «درخواست ایندکس» منتقل می‌کند تا بتوانید برایشان درخواست ایندکس بفرستید.',
      'حداکثر ۲۰۰ آدرس در فرم قرار می‌گیرد؛ اگر بیشتر باشد بقیه را جداگانه باید اضافه کنید.',
      'آدرس‌های ایندکس‌شده ارسال نمی‌شوند.',
    ],
  },
  reinspectAll: {
    title: 'بررسی مجدد وضعیت ایندکس',
    paragraphs: [
      'تمام آدرس‌های همین درخواست را دوباره به فرم «وضعیت ایندکس» منتقل می‌کند.',
      'بعد از انتقال می‌توانید همان لیست را دوباره برای بررسی وضعیت ایندکس ارسال کنید.',
      'حداکثر ۲۰۰ آدرس در فرم قرار می‌گیرد.',
    ],
  },
} as const satisfies Record<InspectBulkAction, BulkActionGuide>;

function overlayContainer() {
  return document.body;
}

function BulkActionHelp({ action }: { action: InspectBulkAction }) {
  const guide = INSPECT_BULK_ACTION_GUIDES[action];
  return (
    <span
      className="history-bulk-help-btn"
      onClick={(event) => event.stopPropagation()}
      onMouseDown={(event) => event.stopPropagation()}
    >
      <HelpTooltip
        html={guideTooltipHtml(guide)}
        placement="left"
        className="ms-1"
      />
    </span>
  );
}

export function InspectBulkMenu({
  menuId,
  indexedCount,
  notIndexedCount,
  totalCount,
  resolveUrls,
  onNotify,
}: {
  menuId: string;
  indexedCount: number;
  notIndexedCount: number;
  totalCount: number;
  resolveUrls: (
    status: 'indexed' | 'notIndexed' | 'all',
  ) => Promise<string[]> | string[];
  onNotify?: BulkNotify;
}) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  async function runAction(action: InspectBulkAction) {
    if (busy) return;
    setBusy(true);
    try {
      const status =
        action === 'copyIndexed'
          ? 'indexed'
          : action === 'reinspectAll'
            ? 'all'
            : 'notIndexed';
      const urls = await resolveUrls(status);

      if (urls.length === 0) {
        onNotify?.({
          variant: 'warning',
          text:
            action === 'copyIndexed'
              ? 'در این درخواست آدرس ایندکس‌شده‌ای نیست.'
              : action === 'reinspectAll'
                ? 'در این درخواست آدرسی نیست.'
                : 'در این درخواست آدرس ایندکس‌نشده‌ای نیست.',
        });
        return;
      }

      if (action === 'requestNotIndexed') {
        const limited = urls.slice(0, INDEX_REQUEST_PREFILL_LIMIT);
        navigateToIndexWithPrefill(navigate, limited, 'inspect-history');
        onNotify?.({
          variant: 'info',
          text:
            limited.length < urls.length
              ? `${formatFaNumber(limited.length)} آدرس ایندکس‌نشده به فرم درخواست ایندکس منتقل شد (حداکثر ${formatFaNumber(INDEX_REQUEST_PREFILL_LIMIT)}).`
              : `${formatFaNumber(limited.length)} آدرس ایندکس‌نشده به فرم درخواست ایندکس منتقل شد.`,
        });
        return;
      }

      if (action === 'reinspectAll') {
        const limited = urls.slice(0, INSPECT_MAX_URLS_PER_JOB);
        navigateToInspectWithPrefill(navigate, limited, 'inspect-bulk-reinspect');
        onNotify?.({
          variant: 'info',
          text:
            limited.length < urls.length
              ? `${formatFaNumber(limited.length)} آدرس به فرم وضعیت ایندکس منتقل شد (حداکثر ${formatFaNumber(INSPECT_MAX_URLS_PER_JOB)}). دوباره ارسال کنید.`
              : `${formatFaNumber(limited.length)} آدرس به فرم وضعیت ایندکس منتقل شد. دوباره ارسال کنید.`,
        });
        return;
      }

      const text = urls.map(decodeUrlForDisplay).join('\n');
      const ok = await copyText(text);
      onNotify?.({
        variant: ok ? 'success' : 'danger',
        text: ok
          ? action === 'copyIndexed'
            ? `${formatFaNumber(urls.length)} آدرس ایندکس‌شده کپی شد.`
            : `${formatFaNumber(urls.length)} آدرس ایندکس‌نشده کپی شد.`
          : 'کپی در کلیپ‌بورد ناموفق بود.',
      });
    } catch (err) {
      onNotify?.({
        variant: 'danger',
        text:
          err instanceof Error
            ? err.message
            : 'خواندن نتایج درخواست ناموفق بود.',
      });
    } finally {
      setBusy(false);
    }
  }

  const menu = (
    <Popover
      id={`inspect-bulk-menu-${menuId}`}
      className="history-bulk-popover"
      onClick={(event) => event.stopPropagation()}
    >
      <Popover.Body className="history-bulk-popover-body">
        <div className="history-bulk-menu-item">
          <button
            type="button"
            className="history-bulk-action"
            disabled={indexedCount === 0 || busy}
            onClick={(event) => {
              event.stopPropagation();
              void runAction('copyIndexed');
            }}
          >
            کپی ایندکس‌شده‌ها
          </button>
          <BulkActionHelp action="copyIndexed" />
        </div>
        <div className="history-bulk-menu-item">
          <button
            type="button"
            className="history-bulk-action"
            disabled={notIndexedCount === 0 || busy}
            onClick={(event) => {
              event.stopPropagation();
              void runAction('copyNotIndexed');
            }}
          >
            کپی ایندکس‌نشده‌ها
          </button>
          <BulkActionHelp action="copyNotIndexed" />
        </div>
        <div className="history-bulk-menu-item">
          <button
            type="button"
            className="history-bulk-action"
            disabled={notIndexedCount === 0 || busy}
            onClick={(event) => {
              event.stopPropagation();
              void runAction('requestNotIndexed');
            }}
          >
            ارسال ایندکس‌نشده‌ها برای ایندکس
          </button>
          <BulkActionHelp action="requestNotIndexed" />
        </div>
        <div className="history-bulk-menu-item">
          <button
            type="button"
            className="history-bulk-action"
            disabled={totalCount === 0 || busy}
            onClick={(event) => {
              event.stopPropagation();
              void runAction('reinspectAll');
            }}
          >
            بررسی مجدد وضعیت ایندکس
          </button>
          <BulkActionHelp action="reinspectAll" />
        </div>
      </Popover.Body>
    </Popover>
  );

  return (
    <OverlayTrigger
      trigger="click"
      rootClose
      placement="bottom-start"
      container={overlayContainer}
      overlay={menu}
    >
      <Button
        variant="outline-secondary"
        size="sm"
        className="history-bulk-menu-toggle d-inline-flex align-items-center me-2 fs--2"
        aria-label="عملیات"
        disabled={busy}
        onClick={(event) => event.stopPropagation()}
        onMouseDown={(event) => event.stopPropagation()}
      >
        {busy ? (
          <Spinner
            size="sm"
            animation="border"
            aria-hidden
            style={{ width: 12, height: 12 }}
          />
        ) : (
          <>
            <IndexerIcon name="collection" className="me-1" />
            <span>عملیات</span>
            <IndexerIcon name="chevron-down" className="ms-1" />
          </>
        )}
      </Button>
    </OverlayTrigger>
  );
}

function urlsFromResults(
  results: ProjectResult[],
  status: 'indexed' | 'notIndexed' | 'all',
): string[] {
  if (status === 'all') {
    return [...new Set(results.map((item) => item.url).filter(Boolean))];
  }
  return inspectUrlsByIndexStatus(results, status);
}

export function InspectJobBulkMenu({
  job,
  projectId,
  onNotify,
}: {
  job: ProjectJob;
  projectId: string;
  onNotify?: BulkNotify;
}) {
  const summary = job.indexStatusSummary;
  const [getProjectJob] = useLazyGetProjectJobQuery();

  return (
    <InspectBulkMenu
      menuId={job.id}
      indexedCount={summary?.indexed ?? 0}
      notIndexedCount={summary?.notIndexed ?? 0}
      totalCount={job.total ?? 0}
      resolveUrls={async (status) => {
        const full = await getProjectJob(
          { projectId, jobId: job.id },
          true,
        ).unwrap();
        return urlsFromResults(full.results ?? [], status);
      }}
      onNotify={onNotify}
    />
  );
}

export function InspectResultsBulkMenu({
  results,
  indexedCount,
  notIndexedCount,
  onNotify,
}: {
  results: ProjectResult[];
  indexedCount: number;
  notIndexedCount: number;
  onNotify?: BulkNotify;
}) {
  return (
    <InspectBulkMenu
      menuId="job-results"
      indexedCount={indexedCount}
      notIndexedCount={notIndexedCount}
      totalCount={results.length}
      resolveUrls={(status) => urlsFromResults(results, status)}
      onNotify={onNotify}
    />
  );
}
